/**
 * Week bucketing for the ranked bands of `/whats-on`.
 *
 * Pure and unit-tested on purpose (same reasoning as `transportCoverage.js`):
 * the page renders exactly what this returns, so the boundary rules are
 * verifiable without mounting anything.
 *
 * The buckets are a partition of ONE ranked feed rather than several queries,
 * so an event cannot land in two of them, and — more importantly — cannot fall
 * between them. A midweek gig has to have somewhere to go: people go out on
 * Tuesdays, and a followed artist playing one is exactly the thing we lose
 * trust by not mentioning.
 */

import { getQuickDateRange, parseLocalDate } from './dateUtils';

/**
 * Partition ranked nearby events into the buckets the page shows.
 *
 * The guarantee is **totality, not chronology**: every event lands in exactly
 * one bucket, so a Tuesday gig cannot silently vanish. It is NOT a
 * chronological partition — `later` deliberately straddles both sides of
 * `nextWeekend`, catching the Mon–Thu window between the two weekends as well
 * as everything beyond. A gig 8 days out therefore renders under "worth
 * planning ahead", below next weekend. That is the honest label: with three
 * buckets there is nowhere truer to put it, and a fourth would sit empty most
 * weeks in a thin market.
 *
 *   now .. this Sunday      -> "This weekend" (Fri-Sun) / "This week" (Mon-Thu)
 *   next Fri .. next Sun    -> "Next weekend"           <- the lead-time bucket
 *   after that              -> "Worth planning ahead"
 *
 * The first bucket's LABEL flips with the day of the week while its window
 * does not: on a Wednesday "this weekend" would be a lie about a Thursday gig,
 * but "this week" is not.
 */
function bucketByWeek(items, todayIso) {
  const weekend = getQuickDateRange('weekend', parseLocalDate(todayIso));
  const next = getQuickDateRange('nextWeekend', parseLocalDate(todayIso));
  const inWeekendNow = weekend.startDate <= todayIso;

  const buckets = {
    soon: { key: inWeekendNow ? 'bucketThisWeekend' : 'bucketThisWeek', items: [] },
    nextWeekend: { key: 'bucketNextWeekend', items: [] },
    later: { key: 'bucketLater', items: [] }
  };

  for (const item of items) {
    // bucket_date when the caller set one (a destination run filed from the
    // day the viewer arrives — tripAwareLabels.bucketDateFor), else the date.
    const d = item.bucket_date || item.event_date;
    if (!d) { buckets.later.items.push(item); continue; }
    if (d <= weekend.endDate) buckets.soon.items.push(item);
    else if (d >= next.startDate && d <= next.endDate) buckets.nextWeekend.items.push(item);
    else buckets.later.items.push(item);
  }
  return buckets;
}

export { bucketByWeek };
export default bucketByWeek;
